/**
 * Industry Adapter Service
 * =========================
 *
 * Automatic translation of API responses to use industry-specific terminology.
 *
 * Features:
 * - Industry-specific terminology mapping
 * - Automatic API response translation
 * - React hooks for industry context
 * - Grammar-aware text transformation
 *
 * Usage:
 *   import { useIndustry, adaptResponse, t } from '@/services/industryAdapter';
 *
 *   // In component
 *   const { industry, terminology, setIndustry } = useIndustry();
 *   const assetTerm = t('asset'); // Returns "device" for healthcare, "equipment" for oil & gas
 *
 *   // Adapt API response
 *   const response = await api.get('/assets');
 *   const adapted = adaptResponse(response, industry);
 */

// =============================================================================
// Industry Terminology Definitions
// =============================================================================

// IndustryId is intentionally `string` so dynamically-registered industries
// (e.g. share_market, energy_storage) work without a code change. The
// builtin list below is just documentation of the canonical 17 + share_market.
export type IndustryId = string;

/** Documented built-in industry codes. New industries via IndustryAdapterService. */
export type BuiltinIndustryId =
  | 'manufacturing'
  | 'oil_gas'
  | 'healthcare'
  | 'financial'
  | 'energy'
  | 'transportation'
  | 'mining'
  | 'chemicals'
  | 'food_beverage'
  | 'pharmaceuticals'
  | 'aerospace'
  | 'metals'
  | 'iot_telecom'
  | 'utilities'
  | 'pulp_paper'
  | 'automotive'
  | 'datacenter'
  | 'share_market';

export interface IndustryTerminology {
  asset: string;
  assetLabel?: string;
  assets: string;
  failure: string;
  failures: string;
  health: string;
  healthy: string;
  degraded: string;
  critical: string;
  maintenance: string;
  facility: string;
  facilities: string;
  operator: string;
  operators: string;
  supervisor: string;
  industryName?: string;
}

export const INDUSTRY_TERMINOLOGY: Record<string, IndustryTerminology> = {
  // share_market — added so OHLCV deployments get tailored UX without
  // forcing the operator to register a custom config first.
  share_market: {
    asset: 'symbol',
    assets: 'symbols',
    failure: 'volatility event',
    failures: 'volatility events',
    health: 'liquidity',
    healthy: 'normal',
    degraded: 'thinning',
    critical: 'halted',
    maintenance: 'rebalance',
    facility: 'exchange',
    facilities: 'exchanges',
    operator: 'trader',
    operators: 'traders',
    supervisor: 'portfolio manager',
    industryName: 'Capital Markets',
  },
  manufacturing: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'operating normally',
    degraded: 'showing wear',
    critical: 'requires immediate attention',
    maintenance: 'maintenance',
    facility: 'plant',
    facilities: 'plants',
    operator: 'operator',
    operators: 'operators',
    supervisor: 'production supervisor',
  },
  oil_gas: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'operating normally',
    degraded: 'degraded',
    critical: 'critical',
    maintenance: 'maintenance',
    facility: 'facility',
    facilities: 'facilities',
    operator: 'operator',
    operators: 'operators',
    supervisor: 'operations supervisor',
  },
  healthcare: {
    asset: 'device',
    assets: 'devices',
    failure: 'malfunction',
    failures: 'malfunctions',
    health: 'status',
    healthy: 'operational',
    degraded: 'degraded performance',
    critical: 'out of service',
    maintenance: 'service',
    facility: 'hospital',
    facilities: 'hospitals',
    operator: 'biomedical technician',
    operators: 'biomedical technicians',
    supervisor: 'clinical engineer',
  },
  financial: {
    asset: 'system',
    assets: 'systems',
    failure: 'outage',
    failures: 'outages',
    health: 'performance',
    healthy: 'performing normally',
    degraded: 'degraded',
    critical: 'critical',
    maintenance: 'maintenance',
    facility: 'data center',
    facilities: 'data centers',
    operator: 'system administrator',
    operators: 'system administrators',
    supervisor: 'IT manager',
  },
  energy: {
    asset: 'turbine',
    assets: 'turbines',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'generating normally',
    degraded: 'underperforming',
    critical: 'offline',
    maintenance: 'maintenance',
    facility: 'wind farm',
    facilities: 'wind farms',
    operator: 'technician',
    operators: 'technicians',
    supervisor: 'site manager',
  },
  transportation: {
    asset: 'vehicle',
    assets: 'vehicles',
    failure: 'breakdown',
    failures: 'breakdowns',
    health: 'condition',
    healthy: 'roadworthy',
    degraded: 'needs service',
    critical: 'out of service',
    maintenance: 'fleet service',
    facility: 'depot',
    facilities: 'depots',
    operator: 'driver',
    operators: 'drivers',
    supervisor: 'fleet manager',
  },
  mining: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'breakdown',
    failures: 'breakdowns',
    health: 'condition',
    healthy: 'operating',
    degraded: 'degraded',
    critical: 'down',
    maintenance: 'maintenance',
    facility: 'mine site',
    facilities: 'mine sites',
    operator: 'equipment operator',
    operators: 'equipment operators',
    supervisor: 'shift supervisor',
  },
  chemicals: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'operating normally',
    degraded: 'showing signs of wear',
    critical: 'requires shutdown',
    maintenance: 'maintenance',
    facility: 'plant',
    facilities: 'plants',
    operator: 'process operator',
    operators: 'process operators',
    supervisor: 'operations supervisor',
  },
  food_beverage: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'operating within specifications',
    degraded: 'showing wear',
    critical: 'requires immediate attention',
    maintenance: 'maintenance',
    facility: 'production facility',
    facilities: 'production facilities',
    operator: 'line operator',
    operators: 'line operators',
    supervisor: 'production supervisor',
  },
  pharmaceuticals: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'deviation',
    failures: 'deviations',
    health: 'GMP status',
    healthy: 'compliant',
    degraded: 'out of specification',
    critical: 'requires validation',
    maintenance: 'maintenance',
    facility: 'manufacturing suite',
    facilities: 'manufacturing suites',
    operator: 'process technician',
    operators: 'process technicians',
    supervisor: 'manufacturing supervisor',
  },
  aerospace: {
    asset: 'aircraft system',
    assets: 'aircraft systems',
    failure: 'failure',
    failures: 'failures',
    health: 'airworthiness',
    healthy: 'airworthy',
    degraded: 'requires inspection',
    critical: 'grounded',
    maintenance: 'maintenance',
    facility: 'hangar',
    facilities: 'hangars',
    operator: 'mechanic',
    operators: 'mechanics',
    supervisor: 'maintenance supervisor',
  },
  metals: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'failure',
    failures: 'failures',
    health: 'condition',
    healthy: 'operating',
    degraded: 'degraded',
    critical: 'shutdown required',
    maintenance: 'maintenance',
    facility: 'mill',
    facilities: 'mills',
    operator: 'operator',
    operators: 'operators',
    supervisor: 'operations supervisor',
  },
  iot_telecom: {
    asset: 'SIM',
    assets: 'SIMs',
    failure: 'connectivity loss',
    failures: 'connectivity losses',
    health: 'signal strength',
    healthy: 'connected',
    degraded: 'weak signal',
    critical: 'offline',
    maintenance: 'profile management',
    facility: 'network region',
    facilities: 'network regions',
    operator: 'NOC engineer',
    operators: 'NOC engineers',
    supervisor: 'network operations manager',
  },
  utilities: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'outage',
    failures: 'outages',
    health: 'condition',
    healthy: 'energized',
    degraded: 'degraded',
    critical: 'de-energized',
    maintenance: 'maintenance',
    facility: 'substation',
    facilities: 'substations',
    operator: 'system operator',
    operators: 'system operators',
    supervisor: 'grid operations manager',
  },
  pulp_paper: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'breakdown',
    failures: 'breakdowns',
    health: 'condition',
    healthy: 'running',
    degraded: 'needs attention',
    critical: 'down',
    maintenance: 'maintenance',
    facility: 'mill',
    facilities: 'mills',
    operator: 'machine tender',
    operators: 'machine tenders',
    supervisor: 'shift superintendent',
  },
  automotive: {
    asset: 'equipment',
    assets: 'equipment',
    failure: 'stoppage',
    failures: 'stoppages',
    health: 'condition',
    healthy: 'operational',
    degraded: 'needs attention',
    critical: 'line down',
    maintenance: 'maintenance',
    facility: 'plant',
    facilities: 'plants',
    operator: 'team member',
    operators: 'team members',
    supervisor: 'group leader',
  },
  datacenter: {
    asset: 'server',
    assetLabel: 'Server',
    assets: 'servers',
    failure: 'system failure',
    failures: 'system failures',
    health: 'system health',
    healthy: 'operational',
    degraded: 'performance degraded',
    critical: 'critical alert',
    maintenance: 'scheduled maintenance',
    facility: 'datacenter',
    facilities: 'datacenters',
    operator: 'infrastructure engineer',
    operators: 'infrastructure engineers',
    supervisor: 'datacenter manager',
    industryName: 'Datacenter',
  },
};

// =============================================================================
// Industry Context
// =============================================================================

let currentIndustry: IndustryId = 'oil_gas'; // Default industry

export const getIndustry = (): IndustryId => currentIndustry;

export const setIndustry = (industry: IndustryId): void => {
  currentIndustry = industry;
  // Store in localStorage for persistence (use same key as IndustryContext)
  if (typeof window !== 'undefined') {
    localStorage.setItem('ryedore-industry', industry);
  }
};

export const loadIndustryFromStorage = (): IndustryId => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem('ryedore-industry');
    if (stored && Object.keys(INDUSTRY_TERMINOLOGY).includes(stored)) {
      currentIndustry = stored as IndustryId;
    }
  }
  return currentIndustry;
};

export const getTerminology = (industry?: IndustryId): IndustryTerminology => {
  const industryId = industry || currentIndustry;
  return INDUSTRY_TERMINOLOGY[industryId];
};

// =============================================================================
// Translation Functions
// =============================================================================

/**
 * Translate a generic term to industry-specific term.
 *
 * @param term - Generic term to translate
 * @param industry - Industry ID (optional, uses current if not provided)
 * @returns Industry-specific term
 */
export const t = (term: keyof IndustryTerminology, industry?: IndustryId): string => {
  const terminology = getTerminology(industry);
  return terminology[term] || term;
};

/**
 * Translate text by replacing generic terms with industry-specific terms.
 *
 * @param text - Text to translate
 * @param industry - Industry ID (optional)
 * @returns Translated text
 */
export const translateText = (text: string, industry?: IndustryId): string => {
  const terminology = getTerminology(industry);
  let translated = text;

  // Replace each term (word boundary aware)
  Object.entries(terminology).forEach(([generic, specific]) => {
    const regex = new RegExp(`\\b${generic}\\b`, 'gi');
    translated = translated.replace(regex, (match) => {
      // Preserve capitalization
      if (match[0] === match[0].toUpperCase()) {
        return specific.charAt(0).toUpperCase() + specific.slice(1);
      }
      return specific;
    });
  });

  return translated;
};

// =============================================================================
// Response Adaptation
// =============================================================================

/**
 * Adapt API response data by translating text fields.
 *
 * @param data - API response data
 * @param industry - Industry ID (optional)
 * @returns Adapted data
 */
export const adaptResponse = <T = any>(data: T, industry?: IndustryId): T => {
  if (!data) return data;

  // Text fields that should be translated
  const TEXT_FIELDS = [
    'description',
    'message',
    'narrative',
    'recommendation',
    'explanation',
    'summary',
    'details',
    'name',
    'title',
    'label',
  ];

  const adapt = (obj: any): any => {
    if (typeof obj === 'string') {
      return translateText(obj, industry);
    }

    if (Array.isArray(obj)) {
      return obj.map(adapt);
    }

    if (obj && typeof obj === 'object') {
      const adapted: any = {};
      Object.keys(obj).forEach((key) => {
        if (TEXT_FIELDS.includes(key) && typeof obj[key] === 'string') {
          adapted[key] = translateText(obj[key], industry);
        } else {
          adapted[key] = adapt(obj[key]);
        }
      });
      return adapted;
    }

    return obj;
  };

  return adapt(data);
};

// =============================================================================
// React Hooks (if using React)
// =============================================================================

import { useState, useEffect, useCallback } from 'react';

export interface UseIndustryReturn {
  industry: IndustryId;
  terminology: IndustryTerminology;
  setIndustry: (industry: IndustryId) => void;
  t: (term: keyof IndustryTerminology) => string;
  translateText: (text: string) => string;
  adaptResponse: <T = any>(data: T) => T;
}

/**
 * React hook for industry context.
 *
 * @returns Industry context with helper functions
 */
export const useIndustry = (): UseIndustryReturn => {
  const [industry, setIndustryState] = useState<IndustryId>(loadIndustryFromStorage());

  useEffect(() => {
    // Sync with global state
    currentIndustry = industry;
    if (typeof window !== 'undefined') {
      localStorage.setItem('industry', industry);
    }
  }, [industry]);

  const terminology = getTerminology(industry);

  const translate = useCallback(
    (term: keyof IndustryTerminology) => terminology[term] || term,
    [terminology]
  );

  const translateTextCallback = useCallback(
    (text: string) => translateText(text, industry),
    [industry]
  );

  const adaptResponseCallback = useCallback(
    <T = any>(data: T) => adaptResponse(data, industry),
    [industry]
  );

  return {
    industry,
    terminology,
    setIndustry: setIndustryState,
    t: translate,
    translateText: translateTextCallback,
    adaptResponse: adaptResponseCallback,
  };
};

/**
 * React hook for terminology translation.
 *
 * @returns Translation function
 */
export const useTranslation = (): ((term: keyof IndustryTerminology) => string) => {
  const { t } = useIndustry();
  return t;
};

// =============================================================================
// API Client Integration
// =============================================================================

/**
 * Create an industry-aware API client that automatically adds industry header
 * and adapts responses.
 *
 * @param baseClient - Base API client (axios, fetch, etc.)
 * @returns Industry-aware API client
 */
export const createIndustryAwareClient = (baseClient: any) => {
  return {
    ...baseClient,
    get: async (url: string, config?: any) => {
      const response = await baseClient.get(url, {
        ...config,
        headers: {
          ...config?.headers,
          'X-Industry-ID': currentIndustry,
        },
      });
      return adaptResponse(response.data, currentIndustry);
    },
    post: async (url: string, data?: any, config?: any) => {
      const response = await baseClient.post(url, data, {
        ...config,
        headers: {
          ...config?.headers,
          'X-Industry-ID': currentIndustry,
        },
      });
      return adaptResponse(response.data, currentIndustry);
    },
    put: async (url: string, data?: any, config?: any) => {
      const response = await baseClient.put(url, data, {
        ...config,
        headers: {
          ...config?.headers,
          'X-Industry-ID': currentIndustry,
        },
      });
      return adaptResponse(response.data, currentIndustry);
    },
    delete: async (url: string, config?: any) => {
      const response = await baseClient.delete(url, {
        ...config,
        headers: {
          ...config?.headers,
          'X-Industry-ID': currentIndustry,
        },
      });
      return adaptResponse(response.data, currentIndustry);
    },
  };
};

// =============================================================================
// Exports
// =============================================================================

export default {
  getIndustry,
  setIndustry,
  loadIndustryFromStorage,
  getTerminology,
  t,
  translateText,
  adaptResponse,
  useIndustry,
  useTranslation,
  createIndustryAwareClient,
  INDUSTRY_TERMINOLOGY,
};
